import { Injectable, inject } from '@angular/core';
import { catchError, share, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BookModel } from '@shared/model/bookModels/book-model';
import { TServiceResult } from '@shared/helper/t-service-result';
import { LocalRepository } from '@shared/helper/local-repository';
import { HttpBaseService } from '../base_services/http_base.service';
import { ShelvesAggrigatorService } from '@app/pages/library/services/shelvesAggrigator.service';
import { bookViewModel } from '@shared/model/new-models/book-view-model';
import { PageList } from '@shared/model/categoryAndSearch/pageList-model';
import { AggregateSearchModel } from '@shared/model/aggreegate-search-model';
import { TermFilterModel } from '@shared/model/categoryAndSearch/term-filter-model';
import { AggregateSearchResultModel } from '@shared/model/aggreegate-search-result-model';
import { suggestForCreateBookModel } from '@shared/model/createBook/suggest-for-create-book-model';
import { SearchTermCreateBookSuggesModel } from '@shared/model/createBook/search-term-create-book-sugges-model';



@Injectable({
  providedIn: 'root'
})

export class SearchService extends HttpBaseService {
  private shelvesAggrigatorService = inject(ShelvesAggrigatorService);


  searched_$ = new BehaviorSubject<boolean>(false);
  searched_OBV = this.searched_$.asObservable();


  constructor() {
    const repo = inject(LocalRepository);
    const http = inject(HttpClient);

    super(repo, http, 'v1/srchAggr');
  }

  searchAggregated(aggregateSearch: AggregateSearchModel): Observable<TServiceResult<AggregateSearchResultModel>> {

    return this.http.post<TServiceResult<AggregateSearchResultModel>>(`${this.apiUrl}/GetByFilter`, aggregateSearch)
      .pipe(
        share()
      )
  }

  grouping(str: string): string {
    let strNew = '';

    const regex = /^\d+$/;
    if (regex.test(str)) {
      return str;
    }

    if (str) {
      for (let i = 0; i < str?.length;) {
        const character = str[i];

        let isRepeated = false;
        let lastOccuranceIndex = i + 1;

        do {
          if (lastOccuranceIndex < str?.length) {
            const newChar = str[lastOccuranceIndex];
            isRepeated = character === newChar;
          }
          else
            isRepeated = false;
          if (isRepeated)
            lastOccuranceIndex++;
        }
        while (isRepeated);
        const countRepeated = lastOccuranceIndex - i + 1;
        if (countRepeated > 2) {
          strNew = strNew + `${character}${character}`;
        }
        else { strNew += `${character}`; }

        i = lastOccuranceIndex;
      }
    }
    else
      strNew = str;

    return strNew;
  }

  searchSuggesstion(term: TermFilterModel, type?: searchTypeEnum): Observable<TServiceResult<AggregateSearchResultModel>> {
    // term.searchTerm = this.grouping(term.searchTerm);
    let termFilter;
    if (type) {
      termFilter = {
        term,
        ObjectType: type
      }
    }
    else {
      termFilter = {
        term
      }
    }

    return this.http.post<TServiceResult<AggregateSearchResultModel>>(`${this.apiUrl}/Suggestion`, termFilter)
      .pipe(
        share()
      )
  }

  suggestForCreateBook(filter: SearchTermCreateBookSuggesModel): Observable<TServiceResult<PageList<suggestForCreateBookModel>>> {
    return this.http.post<TServiceResult<PageList<suggestForCreateBookModel>>>(`${this.apiUrl}/SuggestForCreateBook`, filter)
  }

  /**
   *
   * @param authorId
   * @param TF --> Term Filter
   * @returns AuthorBooksAggrigator
   */
  getByAuthorId(authorId: string, TF?: TermFilterModel, type?: number): Observable<TServiceResult<PageList<bookViewModel>>> {
    return this.http.post<TServiceResult<PageList<bookViewModel>>>(`${this.apiUrl}/GetByAuthorId/${authorId}?type=${type}`, TF)
      .pipe(
        switchMap((res) => {
          if (!res.hasError) {
            return this.shelvesAggrigatorService.injectMyRateState(res.result);
          }
          return of(new TServiceResult<PageList<bookViewModel>>(new PageList(), '', null, true, ''))
        }),
        catchError(() => {
          return of(new TServiceResult<PageList<bookViewModel>>(new PageList(), '', null, true, ''))
        })
      );
  }

  /**
   *
   * @param publisherId
   * @param TF --> Term Filter
   * @returns AuthorBooksAggrigator
   */
  getByPublisherId(publisherId: string, TF?: TermFilterModel): Observable<TServiceResult<PageList<bookViewModel>>> {
    return this.http.post<TServiceResult<PageList<bookViewModel>>>(`${this.apiUrl}/GetByPublisherId/${publisherId}`, TF)
      .pipe(
        switchMap((res) => {
          if (!res.hasError) {
            return this.shelvesAggrigatorService.injectMyRateState(res.result);
          }
          return of(new TServiceResult<PageList<bookViewModel>>(new PageList(), '', null, true, ''))
        })
      );
    ;
  }

  suggestByGenre(titles: string[], termFilter?: TermFilterModel): Observable<TServiceResult<PageList<BookModel>>> {
    const paramsValues = this.TermFilterParams(termFilter)
    return this.http.post<TServiceResult<PageList<BookModel>>>(`${this.apiUrl}/SuggestByGenre/${paramsValues}`, titles);
  }

  suggestRandomByGenre(titles: string[], termFilter?: TermFilterModel): Observable<TServiceResult<PageList<BookModel>>> {
    const paramsValues = this.TermFilterParams(termFilter);
    return this.http.post<TServiceResult<PageList<BookModel>>>(`${this.apiUrl}/SuggestRandomByGenre/${paramsValues}`, titles)
      .pipe(
        switchMap(res => {
          return of(res);
        })
      )
  }

  suggestByGenreIds(ids: string[], termFilter?: TermFilterModel): Observable<TServiceResult<PageList<BookModel>>> {
    const paramsValues = this.TermFilterParams(termFilter)
    return this.http.post<TServiceResult<PageList<BookModel>>>(`${this.apiUrl}/SuggestByGenreId${paramsValues}`, ids);
  }

  suggestRandomByGenreIds(ids: string[], termFilter?: TermFilterModel): Observable<TServiceResult<PageList<BookModel>>> {
    const paramsValues = this.TermFilterParams(termFilter)
    return this.http.post<TServiceResult<PageList<BookModel>>>(`${this.apiUrl}/SuggestRandomByGenreId${paramsValues}`, ids);
  }

  transformIsbnToId(isbn: string): Observable<TServiceResult<BookModel>> {
    return this.http.get<TServiceResult<BookModel>>(`${this.apiUrl}/TransformIsbnToId/${isbn}`)
  }


  getBooksByCategoryId(categoryId: string, tf: TermFilterModel): Observable<TServiceResult<PageList<BookModel>>> {
    return this.http.post<TServiceResult<PageList<BookModel>>>(`${this.apiUrl}/GetByCategoryId/${categoryId}`, tf);
  }

}

export enum searchTypeEnum {
  User = 1,
  Review,
  List,
  Author,
  Book,
  Publisher
}
